import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useState } from 'react';
import {
  useFaqsQuery,
  useRemoveFaqMutation,
  useSaveFaqMutation,
} from '@/queries/faq';
import Portal from '../../modal/Portal';
import { FAQForm } from '../../faq/FAQForm';
import {
  EventRelatedItemRow,
  EventRelatedSection,
} from '../EventRelatedSection';

export function FaqSection({ festivalId }) {
  const faqsQ = useFaqsQuery(festivalId);
  const saveMutation = useSaveFaqMutation(festivalId);
  const removeMutation = useRemoveFaqMutation(festivalId);

  const faqs = faqsQ.data ?? [];
  const loading = festivalId == null || faqsQ.isPending || faqsQ.isFetching;

  const [showModal, setShowModal] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  const openModal = (faq) => {
    const next =
      faq?.faqId != null ? faqs.find((f) => f.faqId === faq.faqId) || {} : {};
    setSelectedFAQ(next);
    setShowModal(true);
  };

  const onFileUpload = (fileInfo) => {
    const { filename, url } = fileInfo;
    setSelectedFAQ((prev) => ({
      ...prev,
      photoURL: url,
      faqPhoto: filename,
    }));
  };

  const onFileDelete = () => {
    setSelectedFAQ((prev) => {
      const next = { ...prev };
      next.photoURL = null;
      next.faqPhoto = null;
      return next;
    });
  };

  const saveFAQ = async (values) => {
    const updatedFAQ = { ...selectedFAQ, ...values };
    try {
      await saveMutation.mutateAsync(updatedFAQ);
      setShowModal(false);
      setSelectedFAQ({});
    } catch {
      /* mutation toast */
    }
  };

  const requestDelete = useCallback((faq) => {
    if (!faq?.faqId) {
      return;
    }
    setPendingDelete(faq);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    const faq = pendingDelete;
    if (!faq?.faqId) {
      return;
    }
    try {
      await removeMutation.mutateAsync(faq.faqId);
      setPendingDelete(null);
      if (selectedFAQ.faqId === faq.faqId) {
        setShowModal(false);
        setSelectedFAQ({});
      }
    } catch {
      /* mutation toast */
    }
  }, [pendingDelete, removeMutation, selectedFAQ.faqId]);

  return (
    <>
      <EventRelatedSection
        icon={['fas', 'circle-question']}
        title="FAQ"
        loading={loading}
        onAdd={() => openModal({})}
        addLabel="Add FAQ"
      >
        {faqs.map((faq) => (
          <EventRelatedItemRow
            key={faq.faqId}
            label={faq.faqName || 'Untitled FAQ'}
            onEdit={() => openModal(faq)}
            onDelete={() => requestDelete(faq)}
          />
        ))}
      </EventRelatedSection>

      <Portal
        header={selectedFAQ?.faqName || 'Add New FAQ'}
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <FAQForm
          faq={selectedFAQ}
          handleSave={saveFAQ}
          loading={loading}
          onFileDelete={onFileDelete}
          onFileUpload={onFileUpload}
        />
      </Portal>

      <Portal
        header="Delete FAQ"
        open={Boolean(pendingDelete)}
        onClose={cancelDelete}
        size="sm"
      >
        <div className="py-2">
          <p className="m-0 text-base leading-relaxed text-[#121212]">
            Are you sure you want to delete this FAQ?
          </p>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-primary bg-transparent px-5 font-bold text-primary transition-all hover:-translate-y-px hover:bg-primary hover:text-primary-foreground"
              onClick={cancelDelete}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] border border-transparent bg-[#c9302c] px-5 font-bold text-white transition-all hover:-translate-y-px hover:brightness-110 disabled:opacity-60"
              onClick={confirmDelete}
              disabled={removeMutation.isPending}
            >
              <FontAwesomeIcon icon={['fas', 'trash']} />
              Delete
            </button>
          </div>
        </div>
      </Portal>
    </>
  );
}
